"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import ScriptPreview, {
  ScriptPreviewSkeleton,
} from "@/components/script/script-preview";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import FilterDropdown from "@/components/script/filter-dropdown";
import { useQuery } from "@tanstack/react-query";
import { env } from "@/env";
import { useFilter } from "@/hooks/use-filter";
import type { PaginatedScripts } from "@/types/script";
import { useDebouncedCallback } from "use-debounce";

type ScriptListClientWrapperProps = {
  initialData: PaginatedScripts;
  initialParams?: {
    query?: string;
    page?: string;
    mobile?: string;
    payment?: string;
    key_system?: string;
    visibility?: string;
    sortBy?: string;
    orderBy?: string;
  };
  useGameRelations?: boolean;
  gameSlug?: string;
};

const ITEMS_PER_PAGE = 16;

export default function ScriptListClientWrapper({
  initialData,
  initialParams,
  useGameRelations = false,
  gameSlug = "",
}: ScriptListClientWrapperProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [searchInput, setSearchInput] = useState(initialParams?.query ?? "");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [windowWidth, setWindowWidth] = useState<number | null>(null);
  const filter = useFilter((state) => state.filters);

  const query = searchParams.get("query") ?? "";
  const currentPage = Math.max(Number(searchParams.get("page")) ?? 1, 1);

  function buildUrl(baseUrl: string, params: Record<string, string | number>) {
    const validParams = Object.entries(params)
      .filter(([_, value]) => value !== "")
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join("&");

    return `${baseUrl}?${validParams}`;
  }

  const fetchParams = {
    query,
    page: currentPage,
    limit: ITEMS_PER_PAGE,
    mobile: filter.mobile ? "true" : "",
    payment: filter.payment ?? "",
    sortBy: filter.sortBy ?? "",
    orderBy:
      filter.orderBy === "asc" || filter.orderBy === "desc"
        ? filter.orderBy
        : "desc",
    key_system: filter.key_system ? "true" : "",
  };

  async function fetchScripts() {
    let url;

    if (useGameRelations && gameSlug) {
      url = buildUrl(`${env.NEXT_PUBLIC_API_URL}/game/${gameSlug}/relations`, {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      });
    } else {
      url = buildUrl(`${env.NEXT_PUBLIC_API_URL}/post/search`, fetchParams);
    }

    try {
      const response = await fetch(url);

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error("Rate limit exceeded. Please try again later.");
        }
        const errorData = await response.json();
        throw new Error(`Failed to fetch scripts: ${errorData.message}`);
      }

      return response.json() as Promise<PaginatedScripts>;
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error("An unexpected error occurred");
    }
  }

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: useGameRelations
      ? ["game-relations", gameSlug, currentPage]
      : [
          "scripts",
          query,
          currentPage,
          filter.mobile,
          filter.payment,
          filter.sortBy,
          filter.orderBy,
          filter.key_system,
        ],
    queryFn: fetchScripts,
    staleTime: 0,
    refetchOnWindowFocus: false,
    initialData: initialData,
  });

  const debouncedSearch = useDebouncedCallback((value: string) => {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("query", value);
    newParams.set("page", "1");
    router.push(`?${newParams.toString()}`);
  }, 300);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setErrorMessage(null);
    debouncedSearch(searchInput);
  }

  function handlePageChange(newPage: number) {
    const validPage = Math.max(newPage, 1);
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("page", validPage.toString());
    router.push(`?${newParams.toString()}`);
  }

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }

    if (typeof window !== "undefined") {
      setWindowWidth(window.innerWidth);
      window.addEventListener("resize", handleResize);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, []);

  useEffect(() => {
    if (useGameRelations) return;

    const params = new URLSearchParams(searchParams.toString());

    if (query) params.set("query", query);
    else params.delete("query");

    params.set("page", currentPage.toString());

    if (filter.mobile) params.set("mobile", "true");
    else params.delete("mobile");

    if (filter.payment) params.set("payment", filter.payment);
    else params.delete("payment");

    if (filter.key_system) params.set("key_system", "true");
    else params.delete("key_system");

    if (filter.sortBy) params.set("sortBy", filter.sortBy);
    else params.delete("sortBy");

    if (
      filter.orderBy &&
      (filter.orderBy === "asc" || filter.orderBy === "desc")
    ) {
      params.set("orderBy", filter.orderBy);
    } else if (filter.orderBy) {
      params.delete("orderBy");
    }

    const newParamsString = params.toString();
    if (newParamsString !== searchParams.toString()) {
      router.push(`?${newParamsString}`);
    }
  }, [filter, query, currentPage, router, searchParams, useGameRelations]);

  const scripts = data?.data.posts.result ?? [];
  const totalPages = data?.data.posts.totalPages ?? 0;

  function generatePaginationItems(currentPage: number, totalPages: number) {
    const items: React.ReactNode[] = [];
    const isMobile = windowWidth !== null && windowWidth < 640;

    if (isMobile) {
      // On mobile, just show current page indicator
      return [
        <PaginationItem key="current-page" className="flex items-center">
          <span className="px-2 text-sm">
            {currentPage} / {totalPages}
          </span>
        </PaginationItem>,
      ];
    }

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(i);
              }}
              isActive={currentPage === i}
            >
              {i}
            </PaginationLink>
          </PaginationItem>,
        );
      }
      return items;
    }

    items.push(
      <PaginationItem key={1}>
        <PaginationLink
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handlePageChange(1);
          }}
          isActive={currentPage === 1}
        >
          1
        </PaginationLink>
      </PaginationItem>,
    );

    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3) {
      startPage = 2;
      endPage = Math.min(totalPages - 1, 3);
    } else if (currentPage >= totalPages - 2) {
      startPage = Math.max(2, totalPages - 2);
      endPage = totalPages - 1;
    }

    if (startPage > 2) {
      items.push(
        <PaginationItem key="ellipsis-1">
          <PaginationEllipsis />
        </PaginationItem>,
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(i);
            }}
            isActive={currentPage === i}
          >
            {i}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    if (endPage < totalPages - 1) {
      items.push(
        <PaginationItem key="ellipsis-2">
          <PaginationEllipsis />
        </PaginationItem>,
      );
    }

    if (totalPages > 1) {
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(totalPages);
            }}
            isActive={currentPage === totalPages}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    return items;
  }

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span>{error.message}</span>
      </div>
    );
  }

  return (
    <>
      {!useGameRelations && (
        <form
          onSubmit={handleSearch}
          className="mt-8 flex w-full flex-row justify-between gap-1.5"
        >
          <div className="relative flex w-full items-center">
            <MagnifyingGlassIcon className="absolute left-5 mr-2 h-4 w-4" />
            <Input
              placeholder="Start typing to search..."
              className="h-12 rounded-3xl pl-10 pr-6"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <Button
              type="submit"
              className="w-18 absolute right-2 h-9 rounded-3xl bg-primary px-4 hover:bg-primary/90"
            >
              Search
            </Button>
          </div>
        </form>
      )}
      {errorMessage && <p className="mt-2 text-destructive">{errorMessage}</p>}

      <div className="mt-8 flex w-full flex-col rounded-md border border-white/5 bg-white/10 backdrop-blur">
        <div className="flex items-center justify-between gap-3 border-b border-white/5 px-6 py-4 lg:px-8">
          <h2 className="font-heading text-balance text-lg font-medium tracking-tight lg:text-xl">
            Page {currentPage}
          </h2>
          {!useGameRelations && <FilterDropdown />}
        </div>

        {isLoading ? (
          <div className="min-h-[600px] w-full p-6 lg:p-8">
            <ul className="grid grid-cols-1 gap-x-1.5 gap-y-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                <ScriptPreviewSkeleton key={`initial-loading-${index}`} />
              ))}
            </ul>
          </div>
        ) : scripts.length > 0 ? (
          <div className="min-h-[600px] w-full p-6 lg:p-8">
            <ul className="grid grid-cols-1 gap-x-1.5 gap-y-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {scripts.map((script, index) => (
                <ScriptPreview key={script.slug} {...script} index={index} />
              ))}
              {isFetching &&
                !isLoading &&
                Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                  <ScriptPreviewSkeleton key={`page-loading-${index}`} />
                ))}
            </ul>
          </div>
        ) : (
          <div className="flex min-h-[600px] w-full items-center justify-center">
            <span>No scripts found.</span>
          </div>
        )}

        {totalPages > 0 && (
          <Pagination className="pb-4">
            <PaginationContent className="flex-wrap justify-center gap-1">
              <PaginationItem>
                <PaginationPrevious
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageChange(Math.max(currentPage - 1, 1));
                  }}
                  href="#"
                  aria-disabled={currentPage <= 1}
                  className={
                    currentPage <= 1
                      ? "pointer-events-none cursor-not-allowed text-muted-foreground"
                      : ""
                  }
                />
              </PaginationItem>

              {generatePaginationItems(currentPage, totalPages)}

              <PaginationItem>
                <PaginationNext
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageChange(Math.min(currentPage + 1, totalPages));
                  }}
                  href="#"
                  aria-disabled={currentPage >= totalPages}
                  className={
                    currentPage >= totalPages
                      ? "pointer-events-none cursor-not-allowed text-muted-foreground"
                      : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
}
